<template lang="pug">
v-row(v-if="contentpage")
	v-col
		h1 {{contentpage.name}}
		ws-content(:value="contentpage.content")
			template(slot="skimaps")
				div.mb-6(v-for="(skimap, index) in skimapList" :key="index")
					h2 {{skimap.caption}}
					a(:href="`${skimap.url}`" target="_blank")
						img(:src="`${skimap.url}?width=1024`" :alt="skimap.alt")
			//- template(slot="interactivemap")
			//- 	client-only
			//- 		mapbox.mt-6(
			//- 			ref="map"
			//- 			style="height:600px"
			//- 			:layers="layers"
			//- 			:sources="sources"
			//- 			:fitbounds="markers.features.map(e=>e.geometry.coordinates)"
			//- 			:mapOptions="customMapOptions ? customMapOptions : mapOptions"
			//- 			@map-styleload="mapStyle" v-on:map-load="mapLoaded")
			//- 			div(slot="popup")
			//- 				h3 {{popupData.name}}
			//- 				v-img(v-if="popupData.photo" :src="`${popupData.photo}?width=400`" max-height="300")
			//- 				div.d-flex(v-if="popupData.reviewCount")
			//- 					v-rating.inline-block(:value="popupData.rating" color="#ffb400" background-color="#ffb400" dense small)
			//- 					span.grey--text ({{popupData.reviewCount}})
</template>

<script>
import gql from 'graphql-tag';
import { ref } from '@vue/composition-api';
import MetaFragment from '@/components/graphql/metaFragment.graphql';
import useMap from '@/use/map';

const getResortSkimaps = gql`
	query getResortSkimaps($slug: String, $path: String, $fallback: String) {
		contentpage(path: $path, fallback: $fallback) {
			id
			name
			slug
			content {
				markdown
			}
			...MetaFragment
		}
		resort(slug: $slug) {
			id
			name
			slug
			geography {
				location {
					coordinates
				}
			}
			country {
				id
				name
				countryCode
				slug
			}
			region {
				id
				name
				slug
			}
			area {
				id
				name
				slug
			}
			areas {
				id
				skimaps {
					url
					alt
					caption
				}
			}
			locations(types: [pisterestaurant, restaurant, apresski, club]) {
				properties
				id
				name
				slug
				photos {
					url
					alt
				}
				resort {
					id
					slug
					name
				}
				reviews {
					voteCount
					average
				}
				geography {
					location {
						type
						coordinates
					}
				}
			}
			skischools {
				id
				name
				resort_name
				url
				partner {
					id
					image
					name
					slug
				}
				geography {
					location {
						type
						coordinates
					}
				}
			}
			rentals {
				id
				name
				city
				discount
				discount_text
				url
				geography {
					location {
						type
						coordinates
					}
				}
				partner {
					id
					image
					name
					slug
				}
			}
		}
	}
	${MetaFragment}
`;

export default {
	nuxtI18n: {
		paths: {
			'nl-NL': '/dorpen/:resort/kaarten',
			'de-DE': '/skiorte/:resort/pistenkarten',
			'nl-BE': '/dorpen/:resort/kaarten'
		}
	},
	layout: 'default',
	setup() {
		const mapItems = ref([]);
		const { markers, sources, attachListeners, popupData, mapOptions } =
			useMap(mapItems);
		const layers = [
			{
				id: 'markers',
				type: 'symbol',
				source: 'geojson-markers',
				layout: {
					'icon-image': ['get', 'icon'],
					'icon-size': 0.3,
					'icon-anchor': 'bottom',
					'icon-allow-overlap': true
				},
				minzoom: 0,
				maxzoom: 24
			}
		];
		return {
			mapItems,
			sources,
			layers,
			markers,
			attachListeners,
			popupData,
			mapOptions
		};
	},
	// eslint-disable-next-line @milqmedia/milq/page-has-meta
	asyncData({ params, app, route }) {
		return app.$graphClient
			.query({
				query: getResortSkimaps,
				variables: {
					slug: params.resort,
					path: route.path,
					fallback: route.path.replace(params.resort, '_resort')
				},
				errorPolicy: 'all'
			})
			.then(({ data, errors }) => {
				if (!data.contentpage) {
					return app.httpError(404, null, 'resorts-resort-skimaps-01', errors);
				}
				if (errors) {
					throw errors[0];
				}

				app.interpolater(data.contentpage.meta, ['title', 'description']);
				app.interpolater(data.contentpage.content, ['markdown']);
				app.interpolater(data.contentpage, ['name']);

				app.meta(data.contentpage.meta);
				app.ads({
					sidebar: {
						visible: true,
						targets: {
							Village: data.resort.id,
							Skiresort: data.resort.area.id,
							State: data.resort.region.id,
							Country: data.resort.country.id
						}
					}
				});
				app.resortBreadcrumbs(data.resort, {
					[app.i18n.t(`page.resorts._resort.skimaps:breadcrumb`)]: {
						name: 'resorts-resort-skimaps',
						params: { resort: data.resort.slug }
					}
				});

				return data;
			})
			.catch(app.$destinationNotFoundCatcher)
			.catch(error => {
				return app.httpError(500, null, 'resorts-resort-skimaps-02', error);
			});
	},
	data() {
		return {
			customMapOptions: null
		};
	},
	head() {
		return {
			...this.$structuredData()
		};
	},
	computed: {
		skimapList() {
			const skimaps = [];
			const areas = this.$lodash.cloneDeep(this.resort.areas);
			areas.forEach(area => {
				area.skimaps.forEach(skimap => {
					skimaps.push(skimap);
				});
			});
			return skimaps.sort((a, b) => a.isDefault + b.isDefault);
		}
	},
	created() {
		this.$emit('update:showSidebar', false);
	},
	mounted() {
		if (!this.resort.locations.length) {
			this.customMapOptions = {
				center: this.resort.geography.location.coordinates,
				zoom: 12
			};
		} else {
			this.mapItems = [
				...this.resort.locations.map(location => {
					const newLocation = this.$lodash.cloneDeep(location);
					newLocation.icon = `${newLocation.properties[0]}-marker`;
					return newLocation;
				}),
				...this.resort.skischools,
				...this.resort.rentals.filter(
					rental => rental.geography.location.coordinates[0] !== 0
				)
			];
		}
	},
	methods: {
		mouseClick(feature) {
			const location = this.resort.locations.find(item => {
				return item.id === feature.properties.id;
			});

			if (!location) {
				window.open(
					`/redir?u=${encodeURIComponent(feature.properties.url)}&p=${
						feature.properties.partnerSlug
					}&i=${feature.properties.id}&t=marker&h=${this.$redirHash(
						feature.properties.url
					)}`
				);
			} else {
				this.$router.push(
					this.localePath({
						name: 'resorts-resort-location',
						params: {
							resort: location.resort.slug,
							location: feature.properties.slug
						}
					})
				);
			}
		},
		mapStyle(map) {
			this.attachListeners(
				map,
				this.$refs.map,
				'markers',
				'geojson-markers',
				this.mouseClick
			);
		},
		mapLoaded() {
			if (this.$refs.map && this.mapItems.length >= 1) {
				this.$refs.map.fitToCurrentBounds();
				if (this.resort.geography.location) {
					this.$refs.map.map.setCenter(
						this.resort.geography.location.coordinates
					);
					this.$refs.map.setZoom(12);
				}
			}
		}
	}
};
</script>
